/**
 * useUserActions — encapsulates user-management API calls
 * (toggle active/suspended, reset password, edit, ban/unban, send reset link,
 * create new user, hard delete).
 *
 * Mirrors the pattern from useStoreActions:
 *   - owns the `processingUser` busy state
 *   - closes modal(s) + clears `selectedUser` BEFORE `await refreshData()`
 *   - thin adapters in ManagePlatform.js bridge to existing modal `onConfirm()`
 */
import { useCallback, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const t = (lang, ar, en) => (lang === 'ar' ? ar : en);

export default function useUserActions({
  token,
  language = 'ar',
  refreshData,
  isCloudGM = false,
  // Cleanup setters
  setSelectedUser,
  setShowResetPasswordModal,
  setNewPassword,
  setShowEditUserModal,
  setShowBanUserModal,
  setBanReason,
  setResetLinkData,
  setShowResetLinkModal,
  setShowAddUserModal,
  setNewUserData,
  setShowDeleteUserModal,
}) {
  const [processingUser, setProcessingUser] = useState(null);
  const headers = useCallback(() => ({ Authorization: `Bearer ${token}` }), [token]);

  // ----- Toggle active / suspended -----
  const toggleUserStatus = useCallback(async (userId, currentStatus) => {
    if (!userId) return;
    const newStatus = currentStatus === 'active' ? 'suspended' : 'active';
    setProcessingUser(userId);
    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/users/${userId}/status`,
        { status: newStatus },
        { headers: headers() },
      );
      toast.success(
        newStatus === 'active'
          ? t(language, 'تم تفعيل الحساب', 'Account activated')
          : t(language, 'تم إيقاف الحساب', 'Account suspended'),
      );
      await refreshData?.();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : t(language, 'فشل في تحديث حالة الحساب', 'Failed to update account status'));
    } finally {
      setProcessingUser(null);
    }
  }, [language, refreshData, headers]);

  // ----- Reset password (admin sets new pw) -----
  const resetUserPassword = useCallback(async ({ user, newPassword }) => {
    if (!user) return;
    if (!newPassword || !newPassword.trim() || newPassword.length < 6) {
      toast.error(t(language, 'كلمة المرور يجب أن تكون 6 أحرف على الأقل', 'Password must be at least 6 characters'));
      return;
    }
    setProcessingUser(user.id);
    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/users/${user.id}/reset-password`,
        { new_password: newPassword },
        { headers: headers() },
      );
      setShowResetPasswordModal?.(false);
      setNewPassword?.('');
      setSelectedUser?.(null);
      toast.success(t(language, 'تم إعادة تعيين كلمة المرور بنجاح', 'Password reset successfully'));
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : t(language, 'فشل في إعادة تعيين كلمة المرور', 'Failed to reset password'));
    } finally {
      setProcessingUser(null);
    }
  }, [language, headers, setShowResetPasswordModal, setNewPassword, setSelectedUser]);

  // ----- Edit user data -----
  const updateUserData = useCallback(async ({ user, data }) => {
    if (!user) return;
    setProcessingUser(user.id);
    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/users/${user.id}`,
        data,
        { headers: headers() },
      );
      setShowEditUserModal?.(false);
      setSelectedUser?.(null);
      toast.success(t(language, 'تم تحديث البيانات بنجاح', 'User data updated successfully'));
      await refreshData?.();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : t(language, 'فشل في تحديث البيانات', 'Failed to update user data'));
    } finally {
      setProcessingUser(null);
    }
  }, [language, refreshData, headers, setShowEditUserModal, setSelectedUser]);

  // ----- Ban / Unban -----
  const toggleBanUser = useCallback(async ({ user, shouldBan = true, reason = '' }) => {
    if (!user) return;
    setProcessingUser(user.id);
    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/users/${user.id}/ban`,
        { banned: shouldBan, reason },
        { headers: headers() },
      );
      setShowBanUserModal?.(false);
      setBanReason?.('');
      setSelectedUser?.(null);
      toast.success(
        shouldBan
          ? t(language, 'تم حظر الحساب بنجاح', 'Account banned successfully')
          : t(language, 'تم إلغاء حظر الحساب', 'Account unbanned successfully'),
      );
      await refreshData?.();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : t(language, 'فشل في تحديث حالة الحظر', 'Failed to update ban status'));
    } finally {
      setProcessingUser(null);
    }
  }, [language, refreshData, headers, setShowBanUserModal, setBanReason, setSelectedUser]);

  // ----- Send reset link (admin-initiated email) -----
  const sendResetLink = useCallback(async ({ user }) => {
    if (!user) return;
    setProcessingUser(user.id);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/admin/users/${user.id}/send-reset-link`,
        {},
        { headers: headers() },
      );
      setResetLinkData?.(response.data);
      setShowResetLinkModal?.(true);
      if (response.data.fallback) {
        toast.warning(t(language, 'فشل إرسال البريد، تم إنشاء الرابط للإرسال يدوياً', 'Email send failed, link created for manual delivery'));
      } else {
        const email = response.data.email_sent_to;
        toast.success(
          language === 'ar'
            ? `تم إرسال رابط إعادة التعيين إلى ${email}`
            : `Reset link sent to ${email}`,
        );
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : t(language, 'فشل في إنشاء رابط إعادة التعيين', 'Failed to create reset link'));
    } finally {
      setProcessingUser(null);
    }
  }, [language, headers, setResetLinkData, setShowResetLinkModal]);

  // ----- Create new user -----
  const createNewUser = useCallback(async ({ newUserData }) => {
    const { first_name, last_name, email, phone } = newUserData || {};
    if (!first_name || !last_name || !email) {
      toast.error(t(language, 'الاسم الأول والأخير والبريد الإلكتروني مطلوبون', 'First name, last name and email are required'));
      return;
    }
    const full_name = `${first_name} ${last_name}`.trim();
    setProcessingUser('new');
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/admin/users/create`,
        {
          name: full_name,
          first_name,
          last_name,
          email,
          phone,
          platform: isCloudGM ? 'cloudgm' : 'cloudgm_business',
        },
        { headers: headers() },
      );

      if (response.data.email_sent) {
        toast.success(
          language === 'ar'
            ? `تم إنشاء الحساب وإرسال رابط التفعيل إلى ${email}`
            : `Account created and activation link sent to ${email}`,
        );
      } else {
        setResetLinkData?.({
          ...response.data,
          reset_link: response.data.reset_link,
          user_email: email,
          fallback: true,
        });
        setShowResetLinkModal?.(true);
        toast.warning(t(
          language,
          'تم إنشاء الحساب، لكن فشل إرسال البريد. يمكنك نسخ الرابط وإرساله يدوياً.',
          'Account created, but email failed. Copy the link and send manually.',
        ));
      }

      setShowAddUserModal?.(false);
      setNewUserData?.({ first_name: '', last_name: '', email: '', phone: '' });
      await refreshData?.();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : t(language, 'فشل في إنشاء الحساب', 'Failed to create account'));
    } finally {
      setProcessingUser(null);
    }
  }, [language, isCloudGM, refreshData, headers, setShowAddUserModal, setNewUserData, setResetLinkData, setShowResetLinkModal]);

  // ----- Hard delete user (cascades children: stores/products/real_estate) -----
  const deleteUser = useCallback(async ({ user }) => {
    if (!user) return;
    setProcessingUser(user.id);
    try {
      const response = await axios.delete(
        `${BACKEND_URL}/api/admin/users/${user.id}`,
        { headers: headers() },
      );
      const deletedData = response.data?.deleted_data;
      let message = t(language, 'تم حذف الحساب بنجاح', 'Account deleted successfully');
      if (deletedData) {
        const items = [];
        if (deletedData.stores > 0) items.push(language === 'ar' ? `${deletedData.stores} متجر` : `${deletedData.stores} store(s)`);
        if (deletedData.products > 0) items.push(language === 'ar' ? `${deletedData.products} منتج` : `${deletedData.products} product(s)`);
        if (deletedData.real_estate > 0) items.push(language === 'ar' ? `${deletedData.real_estate} عقار` : `${deletedData.real_estate} property/ies`);
        if (items.length > 0) {
          message += language === 'ar' ? ` مع ${items.join(' و ')}` : ` along with ${items.join(', ')}`;
        }
      }
      setShowDeleteUserModal?.(false);
      setSelectedUser?.(null);
      toast.success(message);
      await refreshData?.();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : t(language, 'فشل في حذف الحساب', 'Failed to delete account'));
    } finally {
      setProcessingUser(null);
    }
  }, [language, refreshData, headers, setShowDeleteUserModal, setSelectedUser]);

  return {
    processingUser,
    setProcessingUser,
    toggleUserStatus,
    resetUserPassword,
    updateUserData,
    toggleBanUser,
    sendResetLink,
    createNewUser,
    deleteUser,
  };
}
